/**
 * File: diagnostics.ts
 * Path: packages/config/src/diagnostics/diagnostics.ts
 * Description: System > Diagnostics (Liam, 2026-10-08: "to help see if any
 * issue occurs"). Shared by the admin page and the server's `runDiagnostics`.
 *
 * Every finding is a check: ok, a warning, a failure, or just information,
 * with a plain sentence and, when something is wrong, what to do about it.
 * The pure rules that turn raw facts into checks live here so both sides
 * agree and tests can read them.
 */

/** Server-only records of when each timer last ran (`{lastRunAt, ok, note}`). */
export const SYSTEM_HEALTH_COLLECTION = "systemHealth";

export type CheckStatus = "ok" | "warn" | "fail" | "info";

export interface DiagnosticCheck {
  id: string;
  label: string;
  status: CheckStatus;
  /** What was found, in a sentence. */
  detail: string;
  /** What to do, when something is wrong. */
  fix?: string;
}

const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/** The timers that write a heartbeat, and how often each should run. */
export const SCHEDULED_JOBS = {
  sendLeadReminders: {
    label: "Morning leads notification",
    when: "every day at 07:00",
    everyMs: DAY,
  },
  sendDueReminders: {
    label: "Deadline reminders",
    when: "every day at 07:00",
    everyMs: DAY,
  },
  syncTaxReminders: {
    label: "Tax deadline reminders",
    when: "every day at 06:30",
    everyMs: DAY,
  },
  billCarePlans: {
    label: "Care plan invoices",
    when: "every day at 06:00",
    everyMs: DAY,
  },
  sweepStaleDeliveries: {
    label: "Hourly document email check",
    when: "every hour",
    everyMs: HOUR,
  },
} as const;

export type ScheduledJob = keyof typeof SCHEDULED_JOBS;
export const SCHEDULED_JOB_IDS = Object.keys(SCHEDULED_JOBS) as ScheduledJob[];

export const isScheduledJob = (value: unknown): value is ScheduledJob =>
  typeof value === "string" && Object.prototype.hasOwnProperty.call(SCHEDULED_JOBS, value);

export interface JobHeartbeat {
  lastRunAt: number | null;
  ok: boolean;
  /** What the run did, e.g. "Sent to 2 people". */
  note: string;
}

export const toJobHeartbeat = (data: Record<string, unknown> | undefined): JobHeartbeat => ({
  lastRunAt: typeof data?.lastRunAt === "number" ? data.lastRunAt : null,
  ok: data?.ok !== false,
  note: typeof data?.note === "string" ? data.note : "",
});

/** "just now", "5 min ago", "3 h ago", "2 days ago". */
export const agoText = (then: number, now: number): string => {
  const gap = Math.max(0, now - then);
  if (gap < MINUTE) return "just now";
  if (gap < HOUR) return `${Math.floor(gap / MINUTE)} min ago`;
  if (gap < DAY) return `${Math.floor(gap / HOUR)} h ago`;
  const days = Math.floor(gap / DAY);
  return `${days} ${days === 1 ? "day" : "days"} ago`;
};

/**
 * A timer is late once it has missed a whole run, with a little slack for
 * Google's scheduler. A timer that has never written anything may simply not
 * have run since this was deployed, so that is a warning, not a failure.
 */
export const jobCheck = (job: string, beat: JobHeartbeat, now: number): DiagnosticCheck => {
  // The server and the admin are deployed separately: a timer this copy of
  // the admin doesn't know yet is reported, never a crash.
  const info = isScheduledJob(job) ? SCHEDULED_JOBS[job] : { label: `Timer ${job}`, when: "on its schedule", everyMs: Number.POSITIVE_INFINITY };
  const base = { id: `job-${job}`, label: info.label };
  if (beat.lastRunAt === null) {
    return {
      ...base,
      status: "warn",
      detail: `No run recorded yet. It runs ${info.when}.`,
      fix: "If it has been longer than that since the last deploy, check the function in the Firebase console.",
    };
  }
  const ran = `Last ran ${agoText(beat.lastRunAt, now)}${beat.note ? `: ${beat.note}` : ""}.`;
  if (!beat.ok) {
    return { ...base, status: "fail", detail: ran, fix: "Open the function's logs in the Firebase console for the error." };
  }
  if (now - beat.lastRunAt > info.everyMs * 2 + 15 * MINUTE) {
    return {
      ...base,
      status: "fail",
      detail: `${ran} It should run ${info.when}.`,
      fix: "The timer has stopped. Redeploy functions, or check Cloud Scheduler in the Google Cloud console.",
    };
  }
  return { ...base, status: "ok", detail: ran };
};

/** One staff member's notifications, as the server sees them. */
export interface PersonPushHealth {
  uid: string;
  name: string;
  enabled: boolean;
  devices: number;
  /** Devices that confirmed a notification in the last 7 days. */
  workingDevices: number;
  lastConfirmedAt: number | null;
  /** Notifications in the last 7 days, and how many reached no device. */
  sent7d: number;
  missed7d: number;
}

export interface DeliveryHealth {
  /** Quote, audit and brand kit emails in the last 7 days. */
  total: number;
  failed: number;
  /** Still building after the time limit (the hourly check marks these failed). */
  stuck: number;
  recentFailures: Array<{ tool: string; reason: string; at: number }>;
}

export interface EnquiryHealth {
  /** Contact form enquiries in the last 7 days. */
  total: number;
  /** The customer's "we got your message" email failed. */
  confirmationFailed: number;
}

export interface DailyLimit {
  id: string;
  label: string;
  used: number;
  cap: number;
}

/** What `runDiagnostics` answers (admins only). */
export interface SystemReport {
  checkedAt: number;
  /** `job` is a ScheduledJob, unless the server is newer than this admin. */
  jobs: Array<{ job: string; heartbeat: JobHeartbeat }>;
  people: PersonPushHealth[];
  deliveries: DeliveryHealth;
  enquiries: EnquiryHealth;
  limits: DailyLimit[];
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** Turns the server's report into checks, worst news first within each group. */
export const systemChecks = (report: SystemReport): DiagnosticCheck[] => {
  const now = report.checkedAt;
  const checks: DiagnosticCheck[] = report.jobs.map(({ job, heartbeat }) => jobCheck(job, heartbeat, now));

  for (const person of report.people) {
    const id = `push-${person.uid}`;
    const label = `Notifications: ${person.name}`;
    if (!person.enabled) {
      checks.push({ id, label, status: "info", detail: "Notifications are switched off for this person." });
      continue;
    }
    if (person.devices === 0) {
      checks.push({
        id,
        label,
        status: "fail",
        detail: "Notifications are on, but no device is set up, so nothing reaches them.",
        fix: "On their phone: Settings > Notifications > Turn on.",
      });
      continue;
    }
    if (person.workingDevices === 0) {
      checks.push({
        id,
        label,
        status: "warn",
        detail: `${plural(person.devices, "device", "devices")}, none confirmed a notification in the last 7 days.`,
        fix: "Open the admin on each device, then Settings > Notifications > Test all my devices.",
      });
      continue;
    }
    if (person.missed7d > 0) {
      checks.push({
        id,
        label,
        status: "warn",
        detail: `${plural(person.missed7d, "notification", "notifications")} of ${person.sent7d} this week reached no device (they are in the bell).`,
        fix: "Send a test from Settings > Notifications to see which device is failing.",
      });
      continue;
    }
    checks.push({
      id,
      label,
      status: "ok",
      detail: `${plural(person.workingDevices, "device", "devices")} working${
        person.lastConfirmedAt ? `, last confirmed ${agoText(person.lastConfirmedAt, now)}` : ""
      }. ${plural(person.sent7d, "notification", "notifications")} this week.`,
    });
  }

  const d = report.deliveries;
  checks.push(
    d.failed + d.stuck > 0
      ? {
          id: "deliveries",
          label: "Quote, audit and brand kit emails",
          status: "fail",
          detail: `${plural(d.failed + d.stuck, "email", "emails")} of ${d.total} in the last 7 days didn't reach the customer.${
            d.recentFailures[0] ? ` Latest: ${d.recentFailures[0].tool}, ${d.recentFailures[0].reason}.` : ""
          }`,
          fix: "Those customers didn't get their document: send it again from the admin, or contact them.",
        }
      : {
          id: "deliveries",
          label: "Quote, audit and brand kit emails",
          status: "ok",
          detail: `${plural(d.total, "email", "emails")} in the last 7 days, none failed.`,
        },
  );

  const e = report.enquiries;
  checks.push(
    e.confirmationFailed > 0
      ? {
          id: "enquiries",
          label: "Contact form",
          status: "warn",
          detail: `${plural(e.total, "enquiry", "enquiries")} in the last 7 days; ${e.confirmationFailed} didn't get their "we got your message" email. The enquiries themselves were saved.`,
          fix: "Reply to those people yourself, and check Resend for the error.",
        }
      : {
          id: "enquiries",
          label: "Contact form",
          status: "ok",
          detail: `${plural(e.total, "enquiry", "enquiries")} in the last 7 days, all confirmed by email.`,
        },
  );

  for (const limit of report.limits) {
    const share = limit.cap > 0 ? limit.used / limit.cap : 0;
    const detail = `${limit.used} of ${limit.cap} used today.`;
    checks.push(
      share >= 1
        ? { id: `limit-${limit.id}`, label: limit.label, status: "fail", detail, fix: "It resets at 02:00 (midnight UTC). Until then this feature refuses new requests." }
        : share >= 0.8
          ? { id: `limit-${limit.id}`, label: limit.label, status: "warn", detail, fix: "Close to today's limit; it resets at 02:00 (midnight UTC)." }
          : { id: `limit-${limit.id}`, label: limit.label, status: "ok", detail },
    );
  }
  return checks;
};

/** The worst status in a list: what a section's headline shows. */
export const worstStatus = (checks: readonly DiagnosticCheck[]): CheckStatus =>
  checks.some((c) => c.status === "fail")
    ? "fail"
    : checks.some((c) => c.status === "warn")
      ? "warn"
      : "ok";
